/* eslint-disable @typescript-eslint/no-use-before-define */
import { uid } from "@dhx/ts-common/core";
import { el } from "@dhx/ts-common/dom";
import { Id } from "@dhx/ts-common/types";
import { Diagram, ItemConfig, ILineConfig } from "@dhx/ts-diagram";
import { Controls } from "./Controls";

export class Connect {
	private _diagram: Diagram;
	private _nearShape: ItemConfig;
	private _nearPoint: { x: number; y: number; side: string; id: string };
	private _connector: { id: string; from: string; fromSide: string };
	private _controls: Controls;
	private _isOrgType: boolean;

	constructor(controls: Controls, diagram: Diagram) {
		this._controls = controls;
		this._diagram = diagram;
		this._isOrgType = ["org", "mindmap"].includes(this._diagram.config.type);
	}
	getItemId(): Id {
		return this._nearShape ? this._nearShape.id : "";
	}
	getPoints(targetItem, size: any) {
		if (!this._nearShape) {
			return;
		}
		if (!this._nearShape.$item.canResize()) {
			return;
		}
		const grip = this._isOrgType ? 10 : 8;
		const scale = this._diagram.config.scale;

		if (targetItem && targetItem.$item.getBaseType() === "line" && targetItem.$move) {
			const allConnectPoints = this._diagram.data.map(item => {
				const type = item.$item.getBaseType();

				let isOpen = true;
				this._diagram.data.eachParent(item.id, item => {
					if (item?.open === false) isOpen = false;
				});

				if (type !== "shape" || !item.$item.canResize() || !isOpen) {
					return null;
				}
				const box = item.$item.getBox();
				const isGroup = type === "group" || type === "swimlane";
				const width = item[isGroup ? "$width" : "width"] * scale;
				const height = item[isGroup ? "$height" : "height"] * scale;

				return el(
					".dhx_connect",
					{
						style: {
							width,
							height,
							position: "absolute",
							top: (box.top - size.top) * scale,
							left: (box.left - size.left) * scale,
							pointerEvents: "none",
							transform: `rotate(${item.angle || 0}deg)`,
						},
					},
					this._getConnectionPoints(getConnectPoints(item as any, grip), scale)
				);
			});
			return el("div", allConnectPoints);
		}

		const nearType = this._nearShape.$item.getBaseType();
		const box = this._nearShape.$item.getBox();
		const isGroup = nearType === "group" || nearType === "swimlane";
		const width = this._nearShape[isGroup ? "$width" : "width"] * scale;
		const height = this._nearShape[isGroup ? "$height" : "height"] * scale;

		return el(
			".dhx_connect",
			{
				style: {
					width,
					height,
					position: "absolute",
					top: (box.top - size.top) * scale,
					left: (box.left - size.left) * scale,
					pointerEvents: "none",
					transform: `rotate(${this._nearShape.angle || 0}deg)`,
				},
			},
			this._getConnectionPoints(getConnectPoints(this._nearShape as any, grip), scale)
		);
	}
	_getConnectionPoints(points, scale) {
		return points.map((p: any) =>
			el(
				".dhx_connect_point",
				{
					style: {
						top: p.y * scale - 15,
						left: p.x * scale - 15,
					},
					_key: uid(),
					class: `${
						(this._nearPoint && this._nearPoint.side) == p.side && p.id == this._nearPoint.id
							? "dhx_nearest_point"
							: ""
					}`,
					_data: { resizer: this._controls, p },
					onmouseover: [this._setNearPoint, p],
					onmouseleave: [this._removeNearPoint],
					onmousedown: [this.createConnector, p],
				},
				[
					el(".dhx_connect_grip", {
						_data: { resizer: this._controls, p },
					}),
				]
			)
		);
	}
	createConnector = point => {
		const id = uid();
		this._connector = {
			id,
			from: point.id,
			fromSide: point.side,
		};
		const p = this._nearShape.$item.getPoint(point.dx, point.dy);
		this._diagram.data.add({
			id,
			type: "line",
			points: [
				{ x: p.x, y: p.y },
				{ x: p.x, y: p.y },
			],
			stroke: "#2196F3",
			$move: true,
		});
		this._diagram.selection.add(id);
	};
	setNearShape(shape: ItemConfig) {
		if (shape !== this._nearShape) {
			this._nearShape = shape;
			this._diagram.paint();
		}
	}
	toggleNearShape(shape: ItemConfig) {
		this._nearShape = this._nearShape && this._nearShape.id == shape.id ? null : shape;
		this._diagram.paint();
	}
	removeNearShape() {
		this._nearShape = null;
		this._nearPoint = null;
	}
	moveConnector(event, item: ILineConfig, mov: any, p: any) {
		const coords = this._controls.getPoint(event.clientX, event.clientY);
		if (this._connector) {
			const line = this._diagram.data.getItem(this._connector.id);
			line.points[line.points.length - 1] = { x: coords.x, y: coords.y };
			this._diagram.data.update(line.id, line);
			this._diagram.paint();
		}
		this._findNearShape(coords, p);
		let pointSide;

		const newPoints = item.points.map((point, i) => {
			if (!p.$slide && i === p.i) {
				point.x += mov.x;
				point.y += mov.y;
				point.$custom = true;
				if (p.side) {
					pointSide = p.side;
				}
			}
			return point;
		});

		let customGap = 0;

		if (p.$slide) {
			const lastPoint = item.points[item.points.length - 1];
			const prevPoint = item.points[item.points.length - 2];
			customGap =
				p.$slide === "horizontal"
					? Math.abs(lastPoint.x - prevPoint.x - mov.x)
					: Math.abs(lastPoint.y - prevPoint.y - mov.y);
		}

		if (this._nearPoint && pointSide) {
			this._diagram.data.update(item.id, {
				points: newPoints,
				$move: true,
				[pointSide]: this._nearPoint.id,
				[pointSide + "Side"]: this._nearPoint.side,
				customGap,
			});
		} else {
			this._diagram.data.update(item.id, {
				points: newPoints,
				$move: true,
				customGap,
			});
		}
		this._diagram.paint();
	}
	onUp() {
		const item = this._diagram.selection.getItem();
		if (item) {
			delete item.$move;
		}

		if (!item || item.$item.getBaseType() !== "line") {
			return;
		}

		if (!this._nearShape) {
			item?.id && this._diagram.data.remove(item.id);
		} else if (this._connector) {
			const from = this._connector.from;
			const to = this._nearShape.id;

			if (item && from && to && from != to && this._nearPoint) {
				this._diagram.data.update(item.id, {
					from: this._connector.from,
					to: this._nearShape.id,
					fromSide: this._connector.fromSide,
					toSide: this._nearPoint.side,
					points: null,
				});
			} else {
				item?.id && this._diagram.data.remove(item.id);
			}
		}

		this._connector = null;
		this._nearShape = null;
		this._nearPoint = null;
	}
	private _setNearPoint = (p: any) => {
		if (!this._nearPoint || p.side !== this._nearPoint.side) {
			this._nearPoint = p;
			this._diagram.paint();
		}
	};
	private _removeNearPoint = () => {
		if (this._nearPoint) {
			this._nearPoint = null;
			this._diagram.paint();
		}
	};
	private _findNearShape(coords: any, p: any) {
		const shapesByDistance = this._diagram.data
			.map(item => {
				let isOpen = true;
				this._diagram.data.eachParent(item.id, item => {
					if (item?.open === false) isOpen = false;
				});

				if (item.$item.getBaseType() === "shape" && item.id != p.id && isOpen) {
					const itemCenter = {
						x: item.x + item.width / 2,
						y: item.y + item.height / 2,
					};

					return {
						shape: item,
						distance: Math.sqrt(
							Math.pow(coords.x - itemCenter.x, 2) + Math.pow(coords.y - itemCenter.y, 2)
						),
					};
				}
			})
			.filter(s => s)
			.sort((a, b) => a.distance - b.distance)
			.map(s => s.shape);

		this._nearShape = shapesByDistance[0] || this._diagram.data[0];
	}
}

export function getConnectPoints(item: ItemConfig, grip: number) {
	if (!item) {
		return [];
	}
	const type = item.$item.getBaseType();
	const box = item.$item.getBox();
	const isGroup = type === "group" || type === "swimlane";
	const width = item[isGroup ? "$width" : "width"];
	const height = item[isGroup ? "$height" : "height"];

	const x = 0;
	const y = 0;
	const dx = box.left - grip / 2;
	const dy = box.top - grip / 2;
	return [
		{
			dx: dx + width / 2,
			dy: dy + height / 2,
			x: x + width / 2,
			y: y + height / 2,
			id: item.id,
			side: "center",
		},
		{
			x: x + width / 2,
			y,
			dx: dx + width / 2,
			dy,
			id: item.id,
			side: "top",
		},
		{
			x: x + width,
			y: y + height / 2,
			dx: dx + width,
			dy: dy + height / 2,
			id: item.id,
			side: "right",
		},
		{
			x: x + width / 2,
			y: y + height,
			dx: dx + width / 2,
			dy: dy + height,
			id: item.id,
			side: "bottom",
		},
		{
			x,
			y: y + height / 2,
			dx,
			dy: dy + height / 2,
			id: item.id,
			side: "left",
		},
	];
}
